/**
 * Lesson Panel UI — Renders lesson steps dynamically from the schema.
 *
 * Shows: progress bar, narration, questions (MC cards, text input, numeric),
 * explanations, freeform mode with prompt chips + timer, summary screen.
 */
import DOMPurify from 'dompurify'
import LessonEngine, { LessonState } from './lesson-engine.js'
import { getLicenseKey } from '../utils/license.js'
import { getClassroom } from '../utils/classroom.js'

class LessonPanel {
  /**
   * @param {HTMLElement} container - Parent element to attach to
   * @param {Object} deps - Dependencies
   * @param {Function} deps.onCommand - Execute a natural language command
   * @param {Function} deps.routeIntent - Route a pre-built intent
   * @param {Function} deps.parseIntent - Parse a command string to intent
   * @param {Function} deps.speak - Speech synthesis
   * @param {Function} [deps.registerMapClick] - Register a globe click resolver for map_click questions; returns an unregister function
   * @param {Function} [deps.reportStepResult] - Buffer a scored step for D1 persistence (CL-146)
   * @param {Function} [deps.flushResults] - Flush buffered step results at lesson finish/stop (CL-146)
   */
  constructor(container, deps = {}) {
    this.container = container
    this.deps = deps
    this.engine = new LessonEngine()
    this.panel = null
    this._answered = false

    this._build()
    this._wireEngine(deps)
  }

  _build() {
    this.panel = document.createElement('div')
    this.panel.className = 'lesson-panel'
    this.panel.innerHTML = `
      <div class="lesson-panel-header">
        <span class="lesson-panel-title">Lesson Mode</span>
        <button class="lesson-panel-close" id="lesson-close">&times;</button>
      </div>
      <div class="lesson-progress-bar" id="lesson-progress">
        <div class="lesson-progress-fill" id="lesson-progress-fill"></div>
        <span class="lesson-progress-label" id="lesson-progress-label"></span>
      </div>
      <div class="lesson-panel-body" id="lesson-body"></div>
      <div class="lesson-panel-nav" id="lesson-nav">
        <button class="lesson-nav-btn" id="lesson-prev" disabled>Back</button>
        <span class="lesson-step-counter" id="lesson-counter">1 / 1</span>
        <button class="lesson-nav-btn lesson-nav-primary" id="lesson-next">Next</button>
      </div>
    `
    this.container.appendChild(this.panel)

    this.panel.querySelector('#lesson-close').addEventListener('click', () => this.hide())
    this.panel.querySelector('#lesson-prev').addEventListener('click', () => this.engine.previous())
    this.panel.querySelector('#lesson-next').addEventListener('click', () => this._handleNext())
  }

  _wireEngine(deps) {
    // Wire dependencies into engine
    this.engine.routeIntent = deps.routeIntent || null
    this.engine.parseIntent = deps.parseIntent || null
    this.engine.speak = deps.speak || null
    this.engine.gradeOpenResponse = deps.gradeOpenResponse || null
    this.engine.registerMapClick = deps.registerMapClick || null
    // CL-146 (roadmap H2.2): step-result persistence seams — fail-soft,
    // no-ops for anonymous sessions (see src/lessons/result-reporter.js).
    this.engine.reportStepResult = deps.reportStepResult || null
    this.engine.flushResults = deps.flushResults || null

    // Listen to engine events
    this.engine.on('state-changed', ({ to }) => {
      if (to === LessonState.INTRO) this._renderIntro()
      if (to === LessonState.SUMMARY) this._renderSummary()
    })

    this.engine.on('step-changed', ({ index, total, step, isRemediation }) => {
      this._answered = false
      this._renderStep(index, total, step, isRemediation)
    })

    this.engine.on('freeform-tick', ({ remaining }) => {
      this._updateFreeformTimer(remaining)
    })

    // map_click: wrong-click retry feedback ("That's Brazil — try again!")
    this.engine.on('map-click-feedback', ({ message, attemptsLeft }) => {
      this._showMapClickFeedback(message, attemptsLeft)
    })

    // map_click answers arrive from globe clicks, not panel inputs, so no
    // panel handler awaited submitAnswer — render the final result here.
    this.engine.on('question-answered', (result) => {
      if (result?.questionType === 'map_click' && !this._answered) {
        this._answered = true
        this._showQuestionResult(result)
      }
    })
  }

  // ── Public API ─────────────────────────────────────────────

  /**
   * Start a lesson from JSON or URL.
   * @param {string|Object} source - Lesson JSON or URL
   */
  async startLesson(source) {
    this.panel.classList.add('visible')
    this.panel.querySelector('#lesson-nav').style.display = ''

    try {
      await this.engine.load(source)
    } catch (err) {
      this._renderError(err.message)
    }
  }

  /** Hide the panel and stop any active lesson. */
  hide() {
    this.panel.classList.remove('visible')
    if (this.engine.isActive) this.engine.stop()
  }

  /** Expose the engine for external use */
  getEngine() {
    return this.engine
  }

  /**
   * Handle the primary nav button ("Start Lesson" / "Next"). The engine's
   * next() advances from the intro to step 0 and from step to step; question
   * scoring is handled independently when the student answers (see _wireQuestion).
   */
  _handleNext() {
    return this.engine.next()
  }

  // ── Rendering: Intro ───────────────────────────────────────

  _renderIntro() {
    const body = this.panel.querySelector('#lesson-body')
    const nav = this.panel.querySelector('#lesson-nav')
    const lesson = this.engine.lesson
    nav.style.display = ''

    this._updateProgress(0, this.engine.totalSteps)

    const meta = lesson.metadata || {}
    const tags = (meta.tags || []).map((t) => `<span class="lesson-tag">${t}</span>`).join('')

    // SECURITY: sanitized 2026-04-18 — lesson title/description/metadata loaded from R2/URL
    body.innerHTML = DOMPurify.sanitize(`
      <div class="lesson-intro">
        <div class="lesson-intro-title">${lesson.title}</div>
        <div class="lesson-intro-desc">${lesson.description}</div>
        <div class="lesson-intro-meta">
          ${meta.subject ? `<div class="lesson-meta-item">${meta.subject}</div>` : ''}
          <div class="lesson-meta-item">Grade ${meta.grade_band || '?'}</div>
          ${meta.difficulty ? `<div class="lesson-meta-item lesson-difficulty-${meta.difficulty}">${meta.difficulty}</div>` : ''}
          ${meta.duration_minutes ? `<div class="lesson-meta-item">${meta.duration_minutes} min</div>` : ''}
          <div class="lesson-meta-item">${this.engine.totalSteps} steps</div>
          ${this.engine.maxScore > 0 ? `<div class="lesson-meta-item">${this.engine.maxScore} points</div>` : ''}
        </div>
        ${tags ? `<div class="lesson-intro-tags">${tags}</div>` : ''}
        ${meta.standards?.length ? `<div class="lesson-intro-standards">Standards: ${meta.standards.join(', ')}</div>` : ''}
      </div>
    `)

    // Nav shows "Start" button
    const nextBtn = this.panel.querySelector('#lesson-next')
    nextBtn.textContent = 'Start Lesson'
    this.panel.querySelector('#lesson-prev').disabled = true
    this.panel.querySelector('#lesson-counter').textContent = ''
  }

  // ── Rendering: Steps ───────────────────────────────────────

  _renderStep(stepIdx, total, step, isRemediation = false) {
    const body = this.panel.querySelector('#lesson-body')
    const counter = this.panel.querySelector('#lesson-counter')
    const prevBtn = this.panel.querySelector('#lesson-prev')
    const nextBtn = this.panel.querySelector('#lesson-next')

    this._updateProgress(stepIdx + 1, total)
    counter.textContent = `${stepIdx + 1} / ${total}`
    prevBtn.disabled = stepIdx === 0
    nextBtn.textContent = stepIdx === total - 1 ? 'Finish' : 'Next'

    // CL-158 (H2.3 CL-D): remediation steps get supportive framing — a
    // "let's look again" detour, never a punishment. Class + badge only;
    // the step body renders identically.
    let html = `<div class="lesson-step${isRemediation ? ' remediation' : ''}">`
    if (isRemediation) {
      // TIER 1: static string, no interpolation.
      html += `<div class="lesson-remediation-badge">&#128269; Let's look at that another way!</div>`
    }
    html += `<div class="lesson-step-title">${step.title}</div>`

    // Narration text
    if (step.narration?.text) {
      html += `<div class="lesson-narration">${step.narration.text}</div>`
    }

    // Globe action button (for manual triggering or replay)
    if (step.globe_action?.command) {
      html += `
        <button class="lesson-run-btn" id="lesson-run">
          <span class="lesson-run-icon">&#9654;</span> ${step.globe_action.command}
        </button>
      `
    }

    // Question
    if (step.question) {
      html += this._renderQuestion(step.question)
    }

    // Freeform
    if (step.type === 'freeform' && step.freeform) {
      html += this._renderFreeform(step.freeform)
    }

    // Explanation placeholder (shown after answering)
    html += `<div class="lesson-explanation" id="lesson-explanation" style="display:none"></div>`

    html += '</div>'
    // SECURITY: sanitized 2026-04-18 — step.title, narration, question text from R2 lesson
    body.innerHTML = DOMPurify.sanitize(html)

    // Wire run button
    const runBtn = body.querySelector('#lesson-run')
    if (runBtn) {
      runBtn.addEventListener('click', async () => {
        runBtn.disabled = true
        runBtn.innerHTML = '<span class="lesson-run-icon">&#10003;</span> Running...'
        await this.engine._executeGlobeAction(step.globe_action)
      })
    }

    // Wire question interactions
    if (step.question) {
      this._wireQuestion(step.question)
    }

    // Wire freeform prompt chips
    if (step.type === 'freeform') {
      this._wireFreeformChips()
    }
  }

  _renderQuestion(q) {
    let html = `<div class="lesson-quiz">`
    html += `<div class="lesson-quiz-question">${q.prompt}</div>`

    if (q.hint) {
      html += `<div class="lesson-quiz-hint" id="lesson-hint">
        <button class="lesson-hint-btn" id="lesson-hint-btn">Show Hint</button>
        <div class="lesson-hint-text" style="display:none">${q.hint}</div>
      </div>`
    }

    switch (q.type) {
      case 'multiple_choice':
        html += `<div class="lesson-mc-choices" id="lesson-choices">`
        for (const choice of q.choices) {
          html += `<button class="lesson-mc-card" data-choice="${this._escAttr(choice)}">${choice}</button>`
        }
        html += `</div>`
        break

      case 'true_false':
        html += `<div class="lesson-mc-choices" id="lesson-choices">
          <button class="lesson-mc-card" data-choice="true">True</button>
          <button class="lesson-mc-card" data-choice="false">False</button>
        </div>`
        break

      case 'numeric':
        html += `<input type="number" class="lesson-quiz-input" id="lesson-answer" placeholder="Enter a number..." step="any">`
        html += `<button class="lesson-submit-btn" id="lesson-submit-answer">Submit</button>`
        break

      case 'open_response':
      case 'fill_blank':
        html += `<textarea class="lesson-quiz-textarea" id="lesson-answer" placeholder="Type your answer..." rows="3"></textarea>`
        html += `<button class="lesson-submit-btn" id="lesson-submit-answer">Submit</button>`
        break

      case 'map_click':
        html += `<div class="lesson-map-click-hint" id="lesson-map-click-hint">
          <span class="lesson-map-click-icon">&#127757;</span> Click the map! Find it on the globe to answer.
          <div class="lesson-map-click-attempts" id="lesson-map-click-attempts"></div>
        </div>`
        break
    }

    html += `</div>`
    return html
  }

  _wireQuestion(q) {
    const body = this.panel.querySelector('#lesson-body')

    // Hint toggle
    const hintBtn = body.querySelector('#lesson-hint-btn')
    if (hintBtn) {
      hintBtn.addEventListener('click', () => {
        const hintText = body.querySelector('.lesson-hint-text')
        if (hintText) {
          hintText.style.display = hintText.style.display === 'none' ? 'block' : 'none'
          hintBtn.textContent = hintText.style.display === 'none' ? 'Show Hint' : 'Hide Hint'
        }
      })
    }

    // Multiple choice / true-false cards
    const choices = body.querySelectorAll('.lesson-mc-card')
    choices.forEach((card) => {
      card.addEventListener('click', async () => {
        if (this._answered) return
        this._answered = true
        const answer = card.dataset.choice
        const result = await this.engine.submitAnswer(answer)
        this._showQuestionResult(result, choices, card)
      })
    })

    // Submit button for numeric/open response
    const submitBtn = body.querySelector('#lesson-submit-answer')
    if (submitBtn) {
      submitBtn.addEventListener('click', async () => {
        if (this._answered) return
        const input = body.querySelector('#lesson-answer')
        const answer = input?.value?.trim()
        if (!answer) return
        this._answered = true
        submitBtn.disabled = true
        submitBtn.textContent = 'Scoring...'
        const result = await this.engine.submitAnswer(
          q.type === 'numeric' ? Number(answer) : answer
        )
        this._showQuestionResult(result)
      })

      // Enter key submits
      const input = body.querySelector('#lesson-answer')
      if (input && input.tagName !== 'TEXTAREA') {
        input.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') submitBtn.click()
        })
      }
    }
  }

  _showQuestionResult(result, cards, selectedCard) {
    if (!result) return

    const scoring = this.engine.lesson?.scoring || {}
    const showAnswers = scoring.show_correct_answers !== false
    const feedbackMode = scoring.feedback_mode || 'per_question'

    // Highlight MC cards
    if (cards) {
      cards.forEach((card) => {
        card.disabled = true
        if (showAnswers) {
          if (card.dataset.choice === String(result.correctAnswer)) {
            card.classList.add('correct')
          }
          if (card === selectedCard && !result.correct) {
            card.classList.add('incorrect')
          }
        } else if (card === selectedCard) {
          card.classList.add(result.correct ? 'correct' : 'incorrect')
        }
      })
    }

    // Show explanation. CL-139: 'review' is a neutral outcome (fail-closed
    // AI grading) — neither correct nor incorrect; 'partial' earns half
    // credit and renders as correct-with-note.
    if (feedbackMode === 'per_question') {
      const explEl = this.panel.querySelector('#lesson-explanation')
      if (explEl) {
        const verdict = result.verdict || (result.correct ? 'correct' : 'incorrect')
        const statusByVerdict = {
          correct: '&#10003; Correct',
          partial: '&#10003; Partially correct',
          incorrect: '&#10007; Incorrect',
          review: '&#9203; Saved for teacher review',
        }
        const cssByVerdict = { correct: 'correct', partial: 'correct', review: 'review' }
        explEl.style.display = ''
        explEl.className = `lesson-explanation ${cssByVerdict[verdict] || 'incorrect'}`
        // SECURITY: sanitized 2026-04-18 — feedback/explanation may come from grader or lesson JSON
        explEl.innerHTML = DOMPurify.sanitize(`
          <div class="lesson-explanation-status">${statusByVerdict[verdict] || statusByVerdict.incorrect}</div>
          ${result.feedback ? `<div class="lesson-explanation-feedback">${result.feedback}</div>` : ''}
          ${result.explanation ? `<div class="lesson-explanation-text">${result.explanation}</div>` : ''}
        `)
      }
    }
  }

  /** Transient feedback while a map_click question is still retryable. */
  _showMapClickFeedback(message, attemptsLeft) {
    const explEl = this.panel.querySelector('#lesson-explanation')
    if (explEl) {
      explEl.style.display = ''
      explEl.className = 'lesson-explanation incorrect'
      // SECURITY: sanitized 2026-07-01 — message includes clicked feature name from map data
      explEl.innerHTML = DOMPurify.sanitize(
        `<div class="lesson-explanation-feedback">${message}</div>`
      )
    }
    const attemptsEl = this.panel.querySelector('#lesson-map-click-attempts')
    if (attemptsEl && typeof attemptsLeft === 'number') {
      attemptsEl.textContent = attemptsLeft === 1 ? 'Last try!' : `${attemptsLeft} tries left`
    }
  }

  // ── Rendering: Freeform ────────────────────────────────────

  _renderFreeform(config) {
    let html = `<div class="lesson-freeform">`

    if (config.objective) {
      html += `<div class="lesson-freeform-objective">${config.objective}</div>`
    }

    html += `<div class="lesson-freeform-timer" id="lesson-freeform-timer">
      <span class="lesson-timer-icon">&#9201;</span>
      <span class="lesson-timer-value" id="lesson-timer-value">${config.duration_seconds || 120}s</span>
    </div>`

    if (config.suggested_prompts?.length) {
      html += `<div class="lesson-freeform-prompts">
        <div class="lesson-freeform-prompts-label">Try asking:</div>
        <div class="lesson-freeform-chips" id="lesson-chips">`
      for (const prompt of config.suggested_prompts) {
        html += `<button class="lesson-chip" data-prompt="${this._escAttr(prompt)}">${prompt}</button>`
      }
      html += `</div></div>`
    }

    html += `</div>`
    return html
  }

  _wireFreeformChips() {
    const chips = this.panel.querySelectorAll('.lesson-chip')
    chips.forEach((chip) => {
      chip.addEventListener('click', () => {
        const prompt = chip.dataset.prompt
        if (prompt && this.deps.onCommand) {
          this.deps.onCommand(prompt)
          chip.classList.add('used')
        }
      })
    })
  }

  _updateFreeformTimer(remaining) {
    const el = this.panel.querySelector('#lesson-timer-value')
    if (el) {
      const mins = Math.floor(remaining / 60)
      const secs = remaining % 60
      el.textContent = mins > 0 ? `${mins}:${String(secs).padStart(2, '0')}` : `${secs}s`

      if (remaining <= 10) {
        el.classList.add('urgent')
      }
    }
  }

  // ── Rendering: Summary ─────────────────────────────────────

  _renderSummary() {
    const body = this.panel.querySelector('#lesson-body')
    const nav = this.panel.querySelector('#lesson-nav')
    nav.style.display = 'none'

    // CL-150: the engine's getSummary() is the single source of truth for
    // final scoring (review-neutral per CL-139, path-based and latest-attempt
    // per CL-150). Do not re-derive score math here.
    const summary = this.engine.getSummary()
    if (!summary) return

    const timeStr = this._formatTime(summary.elapsedSeconds)
    const hasQuestions = summary.maxScore > 0

    let html = `<div class="lesson-summary">`
    html += `<div class="lesson-summary-emoji">${summary.passed ? '&#127942;' : '&#128170;'}</div>`
    html += `<div class="lesson-summary-title">${summary.passed ? 'Lesson Complete!' : 'Good Effort!'}</div>`
    html += `<div class="lesson-summary-name">${summary.title}</div>`

    html += `<div class="lesson-summary-stats">`
    html += `<div class="lesson-stat"><span class="lesson-stat-label">Steps</span><span class="lesson-stat-value">${summary.completedSteps}/${summary.totalSteps}</span></div>`
    html += `<div class="lesson-stat"><span class="lesson-stat-label">Time</span><span class="lesson-stat-value">${timeStr}</span></div>`

    if (hasQuestions) {
      html += `<div class="lesson-stat"><span class="lesson-stat-label">Score</span><span class="lesson-stat-value">${summary.score}/${summary.effectiveMaxScore} (${summary.percentage}%)</span></div>`
    }
    html += `</div>`

    // CL-139: answers the AI couldn't grade wait in the teacher review queue.
    if (summary.reviewCount > 0) {
      const n = summary.reviewCount
      html += `<div class="lesson-summary-review-note">&#9203; ${n} answer${n === 1 ? '' : 's'} waiting for teacher review</div>`
    }

    // CL-146: results persist only for licensed sessions (result-reporter
    // no-ops otherwise); surface the note only when the student holds a
    // class seat, so their teacher can actually see the results. Static
    // string — nothing dynamic is interpolated.
    if (
      summary.stepResults.length > 0 &&
      this.engine.reportStepResult &&
      getLicenseKey() &&
      getClassroom()
    ) {
      html += `<div class="lesson-summary-saved-note">&#128218; Results saved for your teacher.</div>`
    }

    // Per-question breakdown
    if (summary.stepResults.length > 0) {
      html += `<div class="lesson-results-breakdown">`
      html += `<div class="lesson-results-title">Question Breakdown</div>`
      for (const r of summary.stepResults) {
        const verdict = r.verdict || (r.correct ? 'correct' : 'incorrect')
        const rowClass = verdict === 'review' ? 'review' : r.correct ? 'correct' : 'incorrect'
        const icon = verdict === 'review' ? '&#9203;' : r.correct ? '&#10003;' : '&#10007;'
        const score = verdict === 'review' ? '&#8212;' : `${r.scoreEarned}/${r.maxPoints}`
        html += `<div class="lesson-result-row ${rowClass}">
          <span class="lesson-result-icon">${icon}</span>
          <span class="lesson-result-q">Step ${r.stepIndex + 1}</span>
          <span class="lesson-result-score">${score}</span>
        </div>`
      }
      html += `</div>`
    }

    // Action buttons
    html += `<div class="lesson-summary-actions">`
    if (this.engine.lesson?.scoring?.allow_retry !== false) {
      html += `<button class="lesson-nav-btn lesson-nav-primary" id="lesson-retry">Try Again</button>`
    }
    html += `<button class="lesson-nav-btn" id="lesson-browse">Browse Lessons</button>`
    html += `</div>`

    html += `</div>`
    // SECURITY: sanitized 2026-04-18 — summary.title comes from R2 lesson
    body.innerHTML = DOMPurify.sanitize(html)

    body.querySelector('#lesson-retry')?.addEventListener('click', () => {
      this.engine.retry()
    })
    body.querySelector('#lesson-browse')?.addEventListener('click', () => {
      this.engine.stop()
      this.emit('open-browser')
    })
  }

  // ── Rendering: Error ───────────────────────────────────────

  _renderError(message) {
    const body = this.panel.querySelector('#lesson-body')
    const nav = this.panel.querySelector('#lesson-nav')
    nav.style.display = 'none'

    // SECURITY: sanitized 2026-04-18 — message is a caught error string
    body.innerHTML = DOMPurify.sanitize(`
      <div class="lesson-error">
        <div class="lesson-error-icon">&#9888;</div>
        <div class="lesson-error-title">Failed to Load Lesson</div>
        <div class="lesson-error-msg">${message}</div>
        <button class="lesson-nav-btn" id="lesson-error-back">Back to Browser</button>
      </div>
    `)

    body.querySelector('#lesson-error-back')?.addEventListener('click', () => {
      this.emit('open-browser')
    })
  }

  // ── Progress Bar ───────────────────────────────────────────

  _updateProgress(current, total) {
    const fill = this.panel.querySelector('#lesson-progress-fill')
    const label = this.panel.querySelector('#lesson-progress-label')
    if (fill) {
      const pct = total > 0 ? (current / total) * 100 : 0
      fill.style.width = `${pct}%`
    }
    if (label) {
      label.textContent = total > 0 ? `${current} / ${total}` : ''
    }
  }

  // ── Helpers ────────────────────────────────────────────────

  _formatTime(seconds) {
    const m = Math.floor(seconds / 60)
    const s = seconds % 60
    return m > 0 ? `${m}m ${s}s` : `${s}s`
  }

  _escAttr(str) {
    return String(str).replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  }

  /** Simple event emitter for panel-level events */
  emit(event, data) {
    this.panel.dispatchEvent(new CustomEvent(`lesson-panel:${event}`, { detail: data }))
  }

  onEvent(event, fn) {
    this.panel.addEventListener(`lesson-panel:${event}`, (e) => fn(e.detail))
  }
}

export default LessonPanel
